// useCallLogReview — polls for call_log rows in pending_review status
// belonging to the current user. Fires a custom event when new ones arrive
// so Layout.jsx can open the review modal automatically.

import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

const POLL_INTERVAL_MS = 30_000

export function useCallLogReview() {
  // AuthContext exposes `user`, not `session` — reading `session` here made
  // userId permanently undefined and silently disabled the whole review queue.
  const { user } = useAuth()
  const userId = user?.id
  const [pendingReviews, setPendingReviews] = useState([])
  const [error, setError] = useState(null)
  const seenIds = useRef(new Set())

  const fetchPending = useCallback(async () => {
    if (!userId) return
    try {
      const { data, error: qErr } = await supabase
        .from('call_log')
        .select('id, contact_id, contact_name, raw_caller_number, raw_caller_name, summary, notes, transcript, call_type, duration_minutes, contact_date, source, sentiment, janet_flags, review_status, raw_granola_data, created_at')
        .eq('review_status', 'pending_review')
        // Include unowned rows (Granola owner email didn't match a user) so they aren't invisible
        .or(`created_by.eq.${userId},created_by.is.null`)
        .order('created_at', { ascending: false })
        .limit(50)
      if (qErr) {
        console.warn('[call-review] pending query error:', qErr.message)
        setError(qErr.message)
        return
      }
      setError(null)
      if (!data) return

      setPendingReviews(data)

      // Fire event for any newly-arrived rows the modal hasn't seen yet
      data.forEach(row => {
        if (!seenIds.current.has(row.id)) {
          seenIds.current.add(row.id)
          window.dispatchEvent(new CustomEvent('janet:call-review-needed', { detail: row }))
        }
      })
    } catch { /* non-fatal */ }
  }, [userId])

  useEffect(() => {
    fetchPending()
    const timer = setInterval(fetchPending, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [fetchPending])

  // Called by the modal after a successful confirm so the row disappears
  const markReviewed = useCallback((callLogId) => {
    seenIds.current.add(callLogId)
    setPendingReviews(prev => prev.filter(r => r.id !== callLogId))
  }, [])

  return { pendingReviews, error, markReviewed, refetch: fetchPending }
}
